import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { MessageCircle, Wifi, WifiOff, RefreshCw, Unplug } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const STATUS_BADGE = {
  terkirim: "bg-emerald-50 text-emerald-600 hover:bg-emerald-50",
  gagal: "bg-rose-50 text-rose-600 hover:bg-rose-50",
  antri: "bg-amber-50 text-amber-600 hover:bg-amber-50",
};

function formatWaktu(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function WaGateway() {
  const { user, schoolId } = useAuth();
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [confirmDisc, setConfirmDisc] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [pairing, setPairing] = useState(false);
  const timer = useRef(null);

  const handlePair = async () => {
    setPairing(true);
    try {
      await api.post("/wa/pair");
      toast.success("Pairing dimulai. QR code akan muncul dalam beberapa detik.");
      setTimeout(poll, 3000);
    } catch {
      toast.error("Gagal memulai pairing");
    } finally {
      setPairing(false);
    }
  };

  const poll = async () => {
    try {
      const [s, q, n] = await Promise.all([
        api.get("/wa/status"),
        api.get("/wa/qr"),
        api.get("/wa/notifications").catch(() => ({ data: [] })),
      ]);
      setNeedsSchool(false);
      setStatus(s.data);
      setQr(q.data.qr);
      setNotifs(n.data);
    } catch (e) {
      if (e.response?.status === 400) setNeedsSchool(true);
      else setStatus({ connected: false, gateway_online: false });
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.post("/wa/disconnect");
      toast.success("Koneksi WhatsApp diputus. Pindai QR baru untuk pairing ulang.");
      setConfirmDisc(false);
      setTimeout(poll, 2000);
    } catch {
      toast.error("Gagal memutus koneksi");
    } finally {
      setDisconnecting(false);
    }
  };

  useEffect(() => {
    poll();
    api.get("/schools/current").then((r) => setSchoolName(r.data?.name || "")).catch(() => setSchoolName(""));
    timer.current = setInterval(poll, 10000);
    return () => clearInterval(timer.current);
  }, [schoolId]);

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="wa-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Setiap sekolah memiliki gateway WhatsApp dengan nomor sendiri. Pilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  const connected = status?.connected;
  const online = status?.gateway_online;

  return (
    <div className="space-y-6" data-testid="wa-gateway-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">WhatsApp Gateway</h1>
        <p className="mt-1 text-sm text-slate-500">
          Hubungkan nomor WhatsApp untuk <span className="font-semibold text-slate-700" data-testid="wa-school-name">{schoolName || "sekolah ini"}</span>. Setiap sekolah memakai nomor WA sendiri.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold text-slate-800">Status Koneksi</h3>
            <div className="flex items-center gap-1">
              {connected && (
                <Button variant="outline" size="sm" onClick={() => setConfirmDisc(true)} data-testid="wa-disconnect-button" className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50">
                  <Unplug className="h-3.5 w-3.5" strokeWidth={1.5} /> Putuskan
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={poll} data-testid="wa-refresh-button">
                <RefreshCw className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
              </Button>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3" data-testid="wa-status">
            {connected ? (
              <>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
                  <Wifi className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
                </div>
                <div>
                  <Badge className="rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-50">Terhubung</Badge>
                  <p className="mt-1 text-xs text-slate-500">Nomor: {status?.user?.id?.split(":")[0] || "-"}</p>
                </div>
              </>
            ) : (
              <>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-50">
                  <WifiOff className="h-5 w-5 text-rose-600" strokeWidth={1.5} />
                </div>
                <div>
                  <Badge className="rounded-full bg-rose-50 text-rose-600 hover:bg-rose-50">
                    {online ? "Belum Terhubung" : "Gateway Offline"}
                  </Badge>
                  <p className="mt-1 text-xs text-slate-500">{online ? "Pindai QR code di samping" : "Layanan gateway tidak berjalan"}</p>
                </div>
              </>
            )}
          </div>

          {!connected && qr && (
            <div className="mt-6 rounded-lg border border-slate-200 p-6 text-center" data-testid="wa-qr-container">
              <div className="inline-block rounded-lg bg-white p-3">
                <QRCode value={qr} size={220} />
              </div>
              <p className="mt-3 text-sm font-medium text-slate-700">Pindai dengan WhatsApp</p>
              <p className="mt-1 text-xs text-slate-500">Buka WhatsApp → Perangkat Tertaut → Tautkan Perangkat</p>
              <p className="mt-1 text-xs text-slate-400">QR diperbarui otomatis setiap beberapa detik</p>
            </div>
          )}

          {!connected && !qr && online && status?.initialized === false && (
            <div className="mt-6 rounded-lg border border-dashed border-slate-300 p-6 text-center" data-testid="wa-not-initialized">
              <p className="text-sm text-slate-500">WhatsApp sekolah ini belum pernah dihubungkan.</p>
              <Button onClick={handlePair} disabled={pairing} data-testid="wa-pair-button" className="mt-3 gap-2 bg-[#0060AF] hover:bg-[#004B87]">
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> {pairing ? "Memulai..." : "Hubungkan WhatsApp"}
              </Button>
            </div>
          )}

          {!connected && !qr && online && status?.initialized !== false && (
            <p className="mt-6 text-sm text-slate-500" data-testid="wa-waiting-qr">Menunggu QR code dibuat...</p>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading text-base font-semibold text-slate-800">Cara Kerja</h3>
          <ol className="mt-4 space-y-3 text-sm text-slate-600">
            <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-600">1</span>Siapkan nomor WhatsApp khusus sekolah, lalu pindai QR code dengan nomor tersebut.</li>
            <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-600">2</span>Pastikan kolom "No WA Wali" terisi pada data siswa.</li>
            <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-600">3</span>Setiap transaksi (SPP, tabungan, tagihan) otomatis mengirim notifikasi ke wali.</li>
            <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-600">4</span>Pengingat tunggakan dikirim manual kapan saja dari menu Pemberitahuan → Pengingat Pembayaran.</li>
          </ol>
          <div className="mt-5 rounded-lg bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-700">
            Gateway ini berjalan mandiri tanpa layanan pihak ketiga. Jaga nomor tetap aktif dan jangan logout dari perangkat tertaut.
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
          <h3 className="font-heading text-base font-semibold text-slate-800">Notifikasi Terkirim</h3>
          <span className="ml-auto text-xs text-slate-400">Terfilter sekolah aktif di pemilih sekolah</span>
        </div>
        <div className="mt-4 overflow-x-auto">
          <Table data-testid="wa-notifications-table">
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead>
                <TableHead>Tujuan</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notifs.length === 0 && (
                <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-slate-500">Belum ada notifikasi.</TableCell></TableRow>
              )}
              {notifs.map((n) => (
                <TableRow key={n.id} data-testid={`wa-notif-${n.id}`}>
                  <TableCell className="text-sm text-slate-600">{formatWaktu(n.created_at)}</TableCell>
                  <TableCell className="text-sm text-slate-700">{n.to}</TableCell>
                  <TableCell className="text-sm text-slate-600">{n.kind}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-full ${STATUS_BADGE[n.status] || ""}`} data-testid={`wa-notif-status-${n.id}`}>{n.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <ConfirmDialog
        open={confirmDisc}
        onOpenChange={setConfirmDisc}
        title="Putuskan WhatsApp"
        description={`Yakin ingin memutus koneksi WhatsApp ${schoolName}? Notifikasi tidak akan terkirim sampai Anda pairing ulang dengan QR baru.`}
        onConfirm={handleDisconnect}
        testId="confirm-wa-disconnect"
      />
    </div>
  );
}
